import type { ReactNode } from "react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { ScrollText } from "lucide-react";

export interface LegalSection {
  heading: string;
  body: ReactNode;
}

export function LegalPage({
  eyebrow,
  title,
  intro,
  updated,
  sections,
  footer,
}: {
  eyebrow: string;
  title: string;
  intro: ReactNode;
  updated: string;
  sections: LegalSection[];
  footer?: ReactNode;
}) {
  return (
    <div className="min-h-dvh bg-secondary/30">
      <SiteHeader />
      <div className="container-page py-12 md:py-16">
        <div className="mx-auto max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-trust shadow-card">
            <ScrollText className="h-3.5 w-3.5" />
            {eyebrow}
          </div>
          <h1 className="mt-4 font-display text-4xl text-navy md:text-5xl">{title}</h1>
          <p className="mt-2 text-xs uppercase tracking-wider text-muted-foreground">
            Last updated {updated}
          </p>
          <div className="prose prose-sm mt-6 max-w-none text-navy/90">
            <p className="text-base leading-relaxed text-muted-foreground">{intro}</p>
          </div>

          <div className="mt-10 space-y-8">
            {sections.map((s) => (
              <section
                key={s.heading}
                className="rounded-2xl border border-border bg-white p-6 shadow-card"
              >
                <h2 className="font-display text-xl text-navy">{s.heading}</h2>
                <div className="mt-3 space-y-3 text-sm leading-relaxed text-navy/85">
                  {s.body}
                </div>
              </section>
            ))}
          </div>

          {footer && (
            <div className="mt-8 rounded-2xl border border-border bg-white/70 p-5 text-xs text-muted-foreground">
              {footer}
            </div>
          )}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
